import * as THREE from 'three';

/**
 * A view-culled instance batch (docs/research/MESHY-WORLD-PLAN.md, P3; the pattern of vehicles/wheelBatch.ts
 * made generic): the owner writes one matrix per slot on the CPU (or hides the slot), and once per frame,
 * right before drawing, the render system packs the slots whose bounding sphere touches that frame's view
 * frustum to the front of the InstancedMesh's buffer and draws exactly that many. A slot off screen costs
 * nothing on the GPU. (Not BatchedMesh: without WEBGL_multi_draw, which iOS Safari lacks, three.js falls back
 * to one draw call per instance.)
 *
 * Headless (no render system: tests) `flush(null)` packs every shown slot.
 */
const registry = new Set<CulledInstances>();

/** True while a render system flushes the batches with its frustum each frame (else owners flush themselves). */
export const cullView = { on: false };

const _sphere = new THREE.Sphere();

export class CulledInstances {
  readonly mesh: THREE.InstancedMesh;
  private readonly mats: Float32Array;
  private readonly shown: Uint8Array;
  private high = 0;

  /** `radius`: the bounding sphere (m) round each instance's origin used for the cull (keep a margin for shadows). */
  constructor(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    readonly capacity: number,
    public radius: number,
  ) {
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mats = new Float32Array(capacity * 16);
    this.shown = new Uint8Array(capacity);
    registry.add(this);
  }

  /** Show slot `i` with this world matrix, or hide it (`null`). Takes effect at the next flush. */
  set(i: number, m: THREE.Matrix4 | null): void {
    if (i < 0 || i >= this.capacity) return;
    if (m) {
      this.mats.set(m.elements, i * 16);
      this.shown[i] = 1;
      if (i >= this.high) this.high = i + 1;
    } else this.shown[i] = 0;
  }

  /** Slots drawn after the last flush. */
  get drawn(): number {
    return this.mesh.count;
  }

  /** Pack the shown slots (in `view`, when given) to the front of the instance buffer and draw that many. */
  flush(view: THREE.Frustum | null): void {
    const dst = this.mesh.instanceMatrix.array as Float32Array;
    const src = this.mats;
    _sphere.radius = this.radius;
    let n = 0;
    for (let i = 0; i < this.high; i++) {
      if (!this.shown[i]) continue;
      const o = i * 16;
      if (view) {
        _sphere.center.set(src[o + 12]!, src[o + 13]!, src[o + 14]!);
        if (!view.intersectsSphere(_sphere)) continue;
      }
      dst.set(src.subarray(o, o + 16), n * 16);
      n++;
    }
    this.mesh.count = n;
    if (!n) return;
    const attr = this.mesh.instanceMatrix;
    attr.clearUpdateRanges();
    attr.addUpdateRange(0, n * 16);
    attr.needsUpdate = true;
  }

  dispose(): void {
    registry.delete(this);
    this.mesh.removeFromParent();
    this.mesh.dispose();
  }
}

/** Render system: pack every batch for the frame about to be drawn. */
export function flushCulledInstances(view: THREE.Frustum | null): void {
  for (const b of registry) b.flush(view);
}
